import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { commissionsService, itinerariesService, suppliersService } from '../../services';
import travelieTheme from '../../styles/travelieTheme';
import { useResponsive } from '../../hooks/useResponsive';

// Phase 7: Migrated to TravelieTheme - June 16, 2026

const CommissionForm = ({ onClose, onSuccess, selectedItinerary = null, draftCommission = null }) => {
  const { user } = useAuth();
  const { isMobile } = useResponsive();
  const isEditingDraft = !!draftCommission;

  // Helper function for button styling
  const getButtonStyle = (type, disabled = false) => {
    const baseStyle = {
      padding: `${travelieTheme.spacing.sm} ${travelieTheme.spacing.md}`,
      fontSize: travelieTheme.typography.fontSize.sm,
      fontWeight: travelieTheme.typography.fontWeight.medium,
      borderRadius: travelieTheme.components.card.borderRadius,
      border: 'none',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: `all ${travelieTheme.transitions.fast}`,
      opacity: disabled ? 0.5 : 1
    };

    const styles = {
      primary: { ...baseStyle, background: travelieTheme.colors.primary.main, color: '#FFFFFF' },
      secondary: { ...baseStyle, background: travelieTheme.colors.neutral.border, color: travelieTheme.colors.text.primary }
    };

    return styles[type] || styles.primary;
  };

  // Helper styles for form elements
  const formGroupStyle = {
    marginBottom: travelieTheme.spacing.md
  };

  const labelStyle = {
    display: 'block',
    marginBottom: travelieTheme.spacing.xs,
    fontSize: travelieTheme.typography.fontSize.sm,
    fontWeight: travelieTheme.typography.fontWeight.medium,
    color: travelieTheme.colors.text.primary
  };

  const inputStyle = {
    width: '100%',
    padding: travelieTheme.spacing.sm,
    fontSize: travelieTheme.typography.fontSize.sm,
    border: `1px solid ${travelieTheme.colors.neutral.border}`,
    borderRadius: travelieTheme.components.card.borderRadius,
    outline: 'none',
    transition: `border-color ${travelieTheme.transitions.fast}`
  };
  const isIndependentAgent = user?.role === 'independent_agent';
  const [loading, setLoading] = useState(false);
  const [itineraries, setItineraries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    itinerary_id: draftCommission?.itinerary_id || selectedItinerary?.id || '',
    first_name: draftCommission?.first_name || user?.firstName || user?.first_name || '',
    last_name: draftCommission?.last_name || user?.lastName || user?.last_name || '',
    email: draftCommission?.email || user?.email || '',
    phone: draftCommission?.phone || user?.phone || user?.phone_number || user?.phoneNumber || user?.mobile || user?.business_phone || '',
    reservation_number: draftCommission?.reservation_number || '',
    guest_first_name: draftCommission?.guest_first_name || '',
    guest_last_name: draftCommission?.guest_last_name || '',
    supplier: draftCommission?.supplier || '',
    booking_date: draftCommission?.booking_date || new Date().toISOString().split('T')[0],
    travel_date: draftCommission?.travel_date || '',
    gross_commission: draftCommission?.gross_commission || '',
    notes: draftCommission?.notes || ''
  });

  // Debug logging for phone number population
  useEffect(() => {
    if (user) {
      console.log('📞 CommissionForm - User object:', user);
      console.log('📞 CommissionForm - User phone:', user.phone);
      console.log('📞 CommissionForm - Form phone:', formData.phone);
      
      // Update phone if user has phone data but form phone is empty or whitespace
      const userPhone = user.phone || user.phone_number || user.phoneNumber || user.mobile || user.business_phone;
      if (userPhone && (!formData.phone || formData.phone.trim() === '')) {
        console.log('📞 CommissionForm - Updating form phone from user object:', userPhone);
        setFormData(prev => ({ ...prev, phone: userPhone }));
      }
    }
  }, [user, formData.phone]);

  useEffect(() => {
    fetchItineraries();
    fetchSuppliers();
  }, []);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    // Add modal-open class when component mounts (modal is open)
    document.body.classList.add('modal-open');

    // Cleanup on unmount (modal closes)
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, []);

  const fetchItineraries = async () => {
    try {
      // Fetch completed/archived itineraries (is_archived = 1)
      const data = await itinerariesService.getCompleted();
      // Filter for travel that has actually ended
      const completedItineraries = data.filter(itinerary => 
        new Date(itinerary.travel_end_date) < new Date()
      );
      setItineraries(completedItineraries);
    } catch (error) {
      console.error('Error fetching itineraries:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const data = await suppliersService.getAll();
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.phone || formData.phone.trim() === '') {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.reservation_number) {
      newErrors.reservation_number = 'Reservation number is required';
    }
    if (!formData.guest_first_name) {
      newErrors.guest_first_name = 'Guest first name is required';
    }
    if (!formData.guest_last_name) {
      newErrors.guest_last_name = 'Guest last name is required';
    }
    if (!formData.supplier) {
      newErrors.supplier = 'Supplier is required';
    }
    if (!formData.travel_date) {
      newErrors.travel_date = 'Travel date is required';
    }
    if (!formData.gross_commission || parseFloat(formData.gross_commission) <= 0) {
      newErrors.gross_commission = 'Valid gross commission amount is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;
      
      if (isEditingDraft) {
        // Submitting a draft (convert draft to pending). validateForm() above
        // only checks in-memory formData - submitDraft() takes no body and
        // re-validates against whatever is currently saved in the DB, so any
        // edits made in this form (e.g. filling in a previously-missing
        // travel_date) must be persisted first, or the server re-validates
        // stale data and rejects with a confusing 400 even though the form
        // looks complete on screen.
        const draftData = {
          ...formData,
          gross_commission: formData.gross_commission ? parseFloat(formData.gross_commission) : null
        };
        console.log('💾 CommissionForm - Saving draft edits before submit:', draftCommission.id, draftData);
        await commissionsService.updateDraft(draftCommission.id, draftData);

        // Submitting a draft (convert draft to pending)
        console.log('📝 CommissionForm - Submitting draft ID:', draftCommission.id);
        response = await commissionsService.submitDraft(draftCommission.id);
      } else {
        // Creating new commission as pending
        const submissionData = {
          ...formData,
          gross_commission: parseFloat(formData.gross_commission),
          status: 'pending' // Explicitly set status to pending for submission
        };
        
        // Log submission data for debugging
        console.log('📞 CommissionForm - Submitting data:', submissionData);
        console.log('📞 CommissionForm - Phone in submission:', submissionData.phone);
        
        response = await commissionsService.submit(submissionData);
      }
      
      if (onSuccess) {
        onSuccess(response);
      }
      
      // Reset form
      setFormData({
        itinerary_id: '',
        first_name: user?.firstName || user?.first_name || '',
        last_name: user?.lastName || user?.last_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        reservation_number: '',
        guest_first_name: '',
        guest_last_name: '',
        supplier: '',
        booking_date: new Date().toISOString().split('T')[0],
        travel_date: '',
        gross_commission: '',
        notes: ''
      });
      
      console.log('📞 CommissionForm - Form reset, phone set to:', user?.phone);
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error submitting commission:', error);
      
      // Handle duplicate reservation number error with helpful message
      if (error.response?.data?.error === 'Duplicate reservation number found') {
        setErrors({ 
          submit: `This reservation number already exists in the system.\n\nPlease verify the reservation number is correct, or check if this commission has already been submitted.` 
        });
      } else {
        setErrors({ submit: error.response?.data?.error || 'Failed to submit commission. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAsDraft = async (e) => {
    e.preventDefault();
    
    // For drafts, we don't require all fields - only basic validation
    const draftErrors = {};
    if (!formData.first_name?.trim()) draftErrors.first_name = 'First name is required';
    if (!formData.last_name?.trim()) draftErrors.last_name = 'Last name is required';
    if (!formData.email?.trim()) draftErrors.email = 'Email is required';
    
    if (Object.keys(draftErrors).length > 0) {
      setErrors(draftErrors);
      return;
    }

    setLoading(true);
    try {
      let response;
      const draftData = {
        ...formData,
        gross_commission: formData.gross_commission ? parseFloat(formData.gross_commission) : null
      };
      
      if (isEditingDraft) {
        // Updating existing draft
        console.log('💾 CommissionForm - Updating draft ID:', draftCommission.id, draftData);
        response = await commissionsService.updateDraft(draftCommission.id, draftData);
      } else {
        // Creating new draft
        draftData.status = 'draft'; // Set status to draft for new drafts
        console.log('💾 CommissionForm - Creating new draft:', draftData);
        response = await commissionsService.submit(draftData);
      }
      
      if (onSuccess) {
        onSuccess(response);
      }
      
      // Reset form
      setFormData({
        itinerary_id: '',
        first_name: user?.firstName || user?.first_name || '',
        last_name: user?.lastName || user?.last_name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        reservation_number: '',
        guest_first_name: '',
        guest_last_name: '',
        supplier: '',
        booking_date: new Date().toISOString().split('T')[0],
        travel_date: '',
        gross_commission: '',
        notes: ''
      });
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      setErrors({ submit: error.response?.data?.error || 'Failed to save draft' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle itinerary selection with auto-fill
  const handleItineraryChange = async (itineraryId) => {
    console.log('🔍 CommissionForm - Itinerary selected:', itineraryId);
    
    // Update itinerary_id first
    setFormData(prev => ({ ...prev, itinerary_id: itineraryId }));
    
    if (itineraryId) {
      try {
        console.log('📡 CommissionForm - Fetching itinerary details...');
        // Fetch full itinerary details
        const itinerary = await itinerariesService.getById(itineraryId);
        console.log('✅ CommissionForm - Itinerary data received:', itinerary);
        
        // Parse lead_traveler JSON if available
        let leadTraveler = null;
        if (itinerary.lead_traveler) {
          try {
            leadTraveler = typeof itinerary.lead_traveler === 'string' 
              ? JSON.parse(itinerary.lead_traveler) 
              : itinerary.lead_traveler;
          } catch (e) {
            console.warn('Failed to parse lead_traveler:', e);
          }
        }
        
        // Parse client_info JSON if available
        let clientInfo = null;
        if (itinerary.client_info) {
          try {
            clientInfo = typeof itinerary.client_info === 'string'
              ? JSON.parse(itinerary.client_info)
              : itinerary.client_info;
          } catch (e) {
            console.warn('Failed to parse client_info:', e);
          }
        }
        
        // Extract guest names with priority: lead_traveler > client_info > client_name
        const firstName = leadTraveler?.firstName || clientInfo?.firstName || itinerary.client_name?.split(' ')[0] || '';
        const lastName = leadTraveler?.lastName || clientInfo?.lastName || itinerary.client_name?.split(' ').slice(1).join(' ') || '';
        
        // Format travel_date from ISO to yyyy-MM-dd
        // Handle both ISO datetime strings and plain date strings safely
        let travelDate = '';
        if (itinerary.travel_end_date) {
          const dateStr = String(itinerary.travel_end_date);
          if (dateStr.includes('T')) {
            travelDate = dateStr.split('T')[0];
          } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            travelDate = dateStr;
          } else {
            const dateObj = new Date(dateStr);
            if (!isNaN(dateObj.getTime())) {
              const year = dateObj.getUTCFullYear();
              const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
              const day = String(dateObj.getUTCDate()).padStart(2, '0');
              travelDate = `${year}-${month}-${day}`;
            }
          }
        }
        
        // Auto-populate guest information and travel date
        const autoFillData = {
          itinerary_id: itineraryId,
          guest_first_name: firstName.trim(),
          guest_last_name: lastName.trim(),
          travel_date: travelDate
        };
        
        console.log('📝 CommissionForm - Auto-fill data:', autoFillData);
        
        setFormData(prev => ({
          ...prev,
          ...autoFillData
        }));
        
        // Clear any errors for auto-filled fields
        setErrors(prev => ({
          ...prev,
          guest_first_name: '',
          guest_last_name: '',
          travel_date: ''
        }));
        
        console.log('✨ CommissionForm - Auto-fill complete');
      } catch (error) {
        console.error('❌ CommissionForm - Error fetching itinerary details:', error);
        // If fetch fails, just keep the itinerary_id selected
      }
    }
  };

  // Calculate agent commission preview with proper rounding (rounds .005 up)
  const calculateCommission = (grossCommission, rate) => {
    const value = (parseFloat(grossCommission) * rate) / 100;
    return (Math.round((value + Number.EPSILON) * 100) / 100).toFixed(2);
  };
  
  const agentCommission = formData.gross_commission ? 
    calculateCommission(formData.gross_commission, user?.commissionPercentage || user?.commission_percentage || (isIndependentAgent ? 100 : 70)) : 
    '0.00';

  const isDisneySupplier = (formData.supplier || '').trim().toLowerCase() === 'disney';

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0, 0, 0, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: travelieTheme.spacing.md }} onClick={onClose}>
      <div style={{ background: "#FFFFFF", borderRadius: travelieTheme.components.card.borderRadius, maxWidth: "800px", width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: travelieTheme.typography.fontSize.xl, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>
            {isEditingDraft 
              ? 'Edit Draft Commission' 
              : (isIndependentAgent ? 'Record Commission' : 'Submit Commission Request')
            }
          </h2>
          {onClose && (
            <button 
              onClick={onClose} 
              style={{ background: "none", border: "none", fontSize: "28px", cursor: "pointer", color: travelieTheme.colors.text.secondary, padding: 0, width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}
              type="button"
            >
              <svg style={{ width: "24px", height: "24px" }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} style={{ padding: travelieTheme.spacing.lg }}>
          {errors.submit && (
            <div style={{ 
              padding: travelieTheme.spacing.md, 
              background: "#FEE2E2", 
              border: "2px solid #EF4444", 
              borderRadius: travelieTheme.components.card.borderRadius, 
              marginBottom: travelieTheme.spacing.lg, 
              fontSize: travelieTheme.typography.fontSize.sm, 
              color: "#991B1B",
              whiteSpace: "pre-line",
              lineHeight: "1.6"
            }}>
              <strong>⚠️ Submission Error</strong>
              <div style={{ marginTop: travelieTheme.spacing.sm }}>
                {errors.submit}
              </div>
            </div>
          )}
        {/* Agent Information Section */}
        <div style={{ marginBottom: travelieTheme.spacing.xl, paddingBottom: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          <h3 style={{ margin: 0, marginBottom: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>Agent Information</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginBottom: travelieTheme.spacing.md }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>First Name *</label>
              <input
                type="text"
                value={formData.first_name}
                readOnly
                style={{...inputStyle, background: "#F9FAFB", cursor: "not-allowed"}}
              />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Last Name *</label>
              <input
                type="text"
                value={formData.last_name}
                readOnly
                style={{...inputStyle, background: "#F9FAFB", cursor: "not-allowed"}}
              />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginBottom: travelieTheme.spacing.md }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Email *</label>
              <input
                type="email"
                value={formData.email}
                readOnly
                style={{...inputStyle, background: "#F9FAFB", cursor: "not-allowed"}}
              />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Phone Number *</label>
              <input
                type="tel"
                value={formData.phone}
                readOnly
                style={{...inputStyle, background: "#F9FAFB", cursor: "not-allowed"}}
              />
            </div>
          </div>
        </div>

        {/* Booking Information Section */}
        <div style={{ marginBottom: travelieTheme.spacing.xl, paddingBottom: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          <h3 style={{ margin: 0, marginBottom: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>Booking Information</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginBottom: travelieTheme.spacing.md }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Itinerary (Optional)</label>
              <select
                value={formData.itinerary_id}
                onChange={(e) => handleItineraryChange(e.target.value)}
                style={inputStyle}
              >
                <option value="">Select an itinerary</option>
                {itineraries.map(itinerary => (
                  <option key={itinerary.id} value={itinerary.id}>
                    {itinerary.title} - {itinerary.client_name || 'Unknown Client'}
                  </option>
                ))}
              </select>
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Supplier *</label>
              <select
                value={formData.supplier}
                onChange={(e) => handleInputChange('supplier', e.target.value)}
                style={{...inputStyle, borderColor: errors.supplier ? '#EF4444' : travelieTheme.colors.neutral.border}}
              >
                <option value="">Select a supplier</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.name}>
                    {supplier.name}
                  </option>
                ))}
              </select>
              {errors.supplier && <span style={{ color: "#991B1B", fontSize: travelieTheme.typography.fontSize.sm }}>{errors.supplier}</span>}
              <div style={{ display: "inline-block", marginTop: travelieTheme.spacing.xs, color: travelieTheme.colors.primary.main, textDecoration: "none", fontSize: travelieTheme.typography.fontSize.xs }}>
                <span>Supplier not listed? </span>
                <Link to="/agent/supplier-request" style={{ color: travelieTheme.colors.primary.main, textDecoration: "none", fontSize: travelieTheme.typography.fontSize.sm }}>
                  Please submit your request here
                </Link>
              </div>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginBottom: travelieTheme.spacing.md }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Reservation / Booking Number *</label>
              <input
                type="text"
                value={formData.reservation_number}
                onChange={(e) => handleInputChange('reservation_number', e.target.value)}
                placeholder="Enter reservation number"
                style={{...inputStyle, borderColor: errors.reservation_number ? '#EF4444' : travelieTheme.colors.neutral.border}}
              />
              {errors.reservation_number && <span style={{ color: "#991B1B", fontSize: travelieTheme.typography.fontSize.sm }}>{errors.reservation_number}</span>}
              {isDisneySupplier && (
                <div style={{ marginTop: travelieTheme.spacing.xs, padding: travelieTheme.spacing.sm, border: '1px solid #BFDBFE', borderRadius: travelieTheme.components.card.borderRadius, background: '#EFF6FF', color: '#1E3A8A', fontSize: travelieTheme.typography.fontSize.sm, lineHeight: 1.45 }}>
                  <strong>Walt Disney World Resort hotel-only booking?</strong> Please use the <strong>Travel Plan number</strong>, not the booking confirmation number. The Travel Plan number can be found on the agent copy of the booking confirmation email you received from Disney.
                </div>
              )}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Booking Date *</label>
              <input
                type="date"
                value={formData.booking_date}
                onChange={(e) => handleInputChange('booking_date', e.target.value)}
                style={inputStyle}
              />
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Travel Date *</label>
              <input
                type="date"
                value={formData.travel_date}
                onChange={(e) => handleInputChange('travel_date', e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                style={{...inputStyle, borderColor: errors.travel_date ? '#EF4444' : travelieTheme.colors.neutral.border}}
              />
              {errors.travel_date && <span style={{ color: "#991B1B", fontSize: travelieTheme.typography.fontSize.sm }}>{errors.travel_date}</span>}
            </div>
          </div>
        </div>

        {/* Guest Information Section */}
        <div style={{ marginBottom: travelieTheme.spacing.xl, paddingBottom: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          <h3 style={{ margin: 0, marginBottom: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>Guest Information</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginBottom: travelieTheme.spacing.md }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Guest First Name *</label>
              <input
                type="text"
                value={formData.guest_first_name}
                onChange={(e) => handleInputChange('guest_first_name', e.target.value)}
                placeholder="Enter guest first name"
                style={{...inputStyle, borderColor: errors.guest_first_name ? '#EF4444' : travelieTheme.colors.neutral.border}}
              />
              {errors.guest_first_name && <span style={{ color: "#991B1B", fontSize: travelieTheme.typography.fontSize.sm }}>{errors.guest_first_name}</span>}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Guest Last Name *</label>
              <input
                type="text"
                value={formData.guest_last_name}
                onChange={(e) => handleInputChange('guest_last_name', e.target.value)}
                placeholder="Enter guest last name"
                style={{...inputStyle, borderColor: errors.guest_last_name ? '#EF4444' : travelieTheme.colors.neutral.border}}
              />
              {errors.guest_last_name && <span style={{ color: "#991B1B", fontSize: travelieTheme.typography.fontSize.sm }}>{errors.guest_last_name}</span>}
            </div>
          </div>
        </div>

        {/* Commission Information Section */}
        <div style={{ marginBottom: travelieTheme.spacing.xl, paddingBottom: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          <h3 style={{ margin: 0, marginBottom: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>Commission Information</h3>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: travelieTheme.spacing.md, marginBottom: travelieTheme.spacing.md }}>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Gross Commission ($) *</label>
              <input
                type="number"
                step="0.01"
                value={formData.gross_commission}
                onChange={(e) => handleInputChange('gross_commission', e.target.value)}
                placeholder="0.00"
                style={{...inputStyle, borderColor: errors.gross_commission ? '#EF4444' : travelieTheme.colors.neutral.border}}
              />
              {errors.gross_commission && <span style={{ color: "#991B1B", fontSize: travelieTheme.typography.fontSize.sm }}>{errors.gross_commission}</span>}
            </div>
            <div style={formGroupStyle}>
              <label style={labelStyle}>Agent Commission ({user?.commissionPercentage || user?.commission_percentage || (isIndependentAgent ? 100 : 70)}%)</label>
              <input
                type="text"
                value={`$${agentCommission}`}
                readOnly
                style={{...inputStyle, background: "#F0FDF4", border: "1px solid #86EFAC", cursor: "not-allowed", fontWeight: travelieTheme.typography.fontWeight.semibold, color: "#166534"}}
              />
              <span style={{ fontSize: travelieTheme.typography.fontSize.xs, color: travelieTheme.colors.text.secondary, marginTop: travelieTheme.spacing.xs, fontStyle: "italic" }}>Automatically calculated</span>
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div style={{ marginBottom: travelieTheme.spacing.xl, paddingBottom: travelieTheme.spacing.lg, borderBottom: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          <h3 style={{ margin: 0, marginBottom: travelieTheme.spacing.md, fontSize: travelieTheme.typography.fontSize.lg, fontWeight: travelieTheme.typography.fontWeight.semibold, color: travelieTheme.colors.text.primary }}>Additional Information</h3>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Notes (Optional)</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Enter any additional notes or comments"
              rows="4"
              style={{...inputStyle, resize: 'vertical'}}
            />
          </div>
        </div>

        {Number(user?.agencyId) === 1 && (
          <div style={{ marginBottom: travelieTheme.spacing.lg, padding: travelieTheme.spacing.lg, border: '1px solid #BFDBFE', borderRadius: travelieTheme.components.card.borderRadius, background: '#EFF6FF' }}>
            <div style={{ fontWeight: 700, color: '#1E3A8A', marginBottom: '6px' }}>Enjoying MainStreet Travel Agency?</div>
            <div style={{ color: '#1E40AF', fontSize: travelieTheme.typography.fontSize.sm, marginBottom: '12px' }}>
              If you would like, we would appreciate a 5-star review on Host Agency Reviews. It is completely optional.
            </div>
            <a href="https://hostagencyreviews.com/hosts/mainstreet-travel-agency" target="_blank" rel="noopener noreferrer" style={{ ...getButtonStyle('primary'), display: 'inline-block', textDecoration: 'none' }}>
              Leave a 5-Star Review
            </a>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: travelieTheme.spacing.sm, paddingTop: travelieTheme.spacing.lg, borderTop: `1px solid ${travelieTheme.colors.neutral.border}` }}>
          {onClose && (
            <button 
              type="button" 
              onClick={onClose} 
              style={getButtonStyle("secondary")}
              disabled={loading}
            >
              Cancel
            </button>
          )}
          <button 
            type="button" 
            onClick={handleSaveAsDraft}
            style={{
              ...getButtonStyle("secondary", loading),
              background: '#FEF3C7',
              color: '#92400E',
              border: '1px solid #FCD34D'
            }}
            disabled={loading}
            title="Save commission information without completing all fields"
          >
            {loading ? '💾 Saving...' : '💾 Save as Draft'}
          </button>
          <button 
            type="submit" 
            style={getButtonStyle("primary", loading)}
            disabled={loading}
          >
            {loading 
              ? (isIndependentAgent ? 'Recording...' : 'Submitting...') 
              : (isIndependentAgent ? 'Record Commission' : 'Submit Commission Request')
            }
          </button>
        </div>
      </form>
      </div>
    </div>
  );
};

export default CommissionForm;
